import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, FormControlLabel, Grid, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFetchSalesProfilesDataWrapper } from 'src/app/components/data-wrappers/sales-profile/FetchSalesProfilesDataWrapper';
import { SeatingPlanCategoriesAutocompleteFeature } from 'src/app/components/features/seating-plan-categories/SeatingPlanCategoriesAutocompleteFeature';
import renderPriceInput from 'src/app/components/forms/helpers/renderPriceInput';
import { OptionalPriceInput } from 'src/app/components/forms/prices/OptionalPriceInput';
import { config } from 'src/app/constants/config/config';
import { ticketSalesCapCurrencyOptions } from 'src/app/utilities/mappers/mapCurrenciesToOptions';
import { zodOptionalNumberInputSchema } from 'src/app/utilities/zod/zodOptionalNumberInputSchema';
import { zodOptionalPriceSchema } from 'src/app/utilities/zod/zodOptionalPriceSchema';
import { zodRequiredDropdownSelectSchema } from 'src/app/utilities/zod/zodRequiredDropdownSelectSchema';
import Price from 'src/data/models/common/price';
import { defaultCurrency } from 'src/shared/currencies';
import AutoComplete from 'src/view/components/auto-complete/AutoComplete';
import Checkbox from 'src/view/components/checkbox/Checkbox';
import FormButtons from 'src/view/components/form/FormButtons';
import FormFieldError from 'src/view/components/form/FormFieldError';
import Input from 'src/view/components/input/Input';
import { LinkActionButton } from 'src/view/components/link-action-button/LinkActionButton';
import LoadingOverlay from 'src/view/components/loading-overlay/LoadingOverlay';
import ModalSubTitle from 'src/view/components/modal/ModalSubtitle';
import z from 'zod';
import useFetchEventBookingOptions from '../features/event-booking-options/hooks/use-fetch-event-booking-options';

interface PriceError {
    value: {
        message?: string;
        type: string;
    };
}

const useStyles = makeStyles((theme: Theme) => ({
    spacingRight: {
        paddingRight: theme.spacing(1),
    },
    smallFontSize: {
        '& span': {
            fontSize: theme.typography.pxToRem(14),
        },
    },
    blockHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    samePriceCheckbox: {
        marginRight: 0,
        '& .MuiCheckbox-root': {
            padding: theme.spacing(0.5),
        },
    },
    salesProfilePrice: {
        '&:not(:last-child)': {
            marginBottom: theme.spacing(1),
        },
    },
}));

const requiredNumberWithAllowedZeroInputSchema = z
    .string({ required_error: 'Please enter a number' })
    .nonempty('Please enter a number')
    .or(z.number())
    .pipe(z.coerce.number({ invalid_type_error: 'Please enter a number!' }));

const priceInputSchema = z.custom<Price>((price) => typeof price === 'object' && price !== null);

const addPriceIssue = (price: Price, path: (string | number)[], ctx: z.RefinementCtx) => {
    if (price.value === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'value'],
            message: 'Please enter a price',
        });
        return;
    }

    if (price.value <= 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'value'],
            message: 'Please enter a higher amount than 0',
        });
    }
};

const validationSchema = z
    .object({
        seatingPlanCategory: zodRequiredDropdownSelectSchema,
        bookingOption: zodRequiredDropdownSelectSchema,
        useSamePrice: z.boolean(),
        retailPrice: priceInputSchema,
        salesProfilePrices: z.array(
            z.object({ salesProfileId: z.string(), price: priceInputSchema })
        ),
        expectedPurchasePrice: zodOptionalPriceSchema,
        salesCap: requiredNumberWithAllowedZeroInputSchema,
        /** fixedQuantityStep is optional to be entered, but if nothing is entered then we default to 1 */
        fixedQuantityStep: zodOptionalNumberInputSchema.transform((value) => value || 1),
        salesProfiles: z.array(zodRequiredDropdownSelectSchema),
    })
    .superRefine((values, ctx) => {
        if (values.useSamePrice) {
            addPriceIssue(values.retailPrice, ['retailPrice'], ctx);
            return;
        }

        values.salesProfilePrices.forEach(({ price }, index) =>
            addPriceIssue(price, ['salesProfilePrices', index, 'price'], ctx)
        );
    });

export type CreateTicketSalesCapFormValues = z.infer<typeof validationSchema>;

interface Props {
    eventId: string;
    loading?: boolean;
    defaultValues?: CreateTicketSalesCapFormValues;
    editMode?: boolean;
    onFormSubmit: (values: CreateTicketSalesCapFormValues) => void;
}

export default function CreateTicketSalesCapForm({
    eventId,
    loading,
    defaultValues,
    editMode,
    onFormSubmit,
}: Props) {
    const classes = useStyles();

    const form = useForm<CreateTicketSalesCapFormValues>({
        mode: 'onChange',
        resolver: zodResolver(validationSchema),
        defaultValues: {
            useSamePrice: true,
            retailPrice: { value: undefined, currency: defaultCurrency },
            salesProfilePrices: [],
            expectedPurchasePrice: { value: undefined, currency: defaultCurrency },
        },
    });

    const { setValue, getValues, watch, control, reset, handleSubmit } = form;

    const salesProfiles = watch('salesProfiles');
    const useSamePrice = watch('useSamePrice');
    const salesProfilePrices = watch('salesProfilePrices');

    const { data: salesProfilesData } = useFetchSalesProfilesDataWrapper({
        pageSize: config.ITEMS_PER_PAGE_LARGE,
    });

    const salesProfileOptions = useMemo(
        () => (salesProfilesData?.data ?? []).map((p) => ({ label: p.name, value: p.id })),
        [salesProfilesData]
    );

    const pricedSalesProfileIds = useMemo(
        () => (salesProfiles ?? []).map((p) => p.value),
        [salesProfiles]
    );

    useEffect(() => {
        const currentPrices = getValues('salesProfilePrices') ?? [];
        const isInSync =
            currentPrices.length === pricedSalesProfileIds.length &&
            currentPrices.every(
                (row, index) => row.salesProfileId === pricedSalesProfileIds[index]
            );

        if (isInSync) return;

        const firstFilledPrice = currentPrices.find((row) => row.price.value !== undefined)?.price;

        setValue(
            'salesProfilePrices',
            pricedSalesProfileIds.map(
                (salesProfileId) =>
                    currentPrices.find((row) => row.salesProfileId === salesProfileId) ?? {
                        salesProfileId,
                        price: firstFilledPrice ?? { value: undefined, currency: defaultCurrency },
                    }
            )
        );
    }, [pricedSalesProfileIds, getValues, setValue]);

    const onUseSamePriceChange = (checked: boolean) => {
        const currentPrices = getValues('salesProfilePrices');

        if (checked) {
            const firstFilledPrice = currentPrices.find(
                (row) => row.price.value !== undefined
            )?.price;

            if (firstFilledPrice) setValue('retailPrice', firstFilledPrice);
        } else {
            const retailPrice = getValues('retailPrice');

            if (retailPrice.value !== undefined) {
                setValue(
                    'salesProfilePrices',
                    currentPrices.map((row) => ({ ...row, price: retailPrice }))
                );
            }
        }

        setValue('useSamePrice', checked);
    };

    const isSubmitButtonDisabled = !salesProfiles?.length;

    const { data: bookingOptionsData, error } = useFetchEventBookingOptions(eventId);

    const availableBookingOptions = useMemo(() => {
        const bookingOptions = bookingOptionsData?.data?.data || [];

        return bookingOptions.map(({ name, id }) => ({
            label: name,
            value: id,
        }));
    }, [bookingOptionsData]);

    const hasOnlyOneBookingOption = availableBookingOptions.length === 1;

    useEffect(() => {
        if (!defaultValues) return;

        reset(defaultValues);
    }, [defaultValues, reset]);

    useEffect(() => {
        if (editMode) return;

        // If there is only one booking option, select it by default
        if (hasOnlyOneBookingOption) {
            setValue('bookingOption', availableBookingOptions[0]);
        }
    }, [editMode, availableBookingOptions, hasOnlyOneBookingOption]);

    if (error) {
        return (
            <Alert severity="error">
                <span>
                    There was a problem while retrieving booking options. This makes creating a
                    ticket sales cap impossible. Please try again in a few minutes.
                </span>
            </Alert>
        );
    }

    return (
        <>
            {loading && <LoadingOverlay />}
            <Grid container rowSpacing={2}>
                <Grid item xs={12}>
                    <ModalSubTitle>Seating Plan Category*</ModalSubTitle>
                    <Controller
                        name="seatingPlanCategory"
                        control={control}
                        render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <>
                                <SeatingPlanCategoriesAutocompleteFeature
                                    name={name}
                                    eventId={eventId}
                                    value={value}
                                    onChange={onChange}
                                    placeholder="Search for a seatingplancategory"
                                    disabled={editMode}
                                    hideArchivedSpcs
                                    enableCreation
                                />
                                <FormFieldError message={error?.message} />
                            </>
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ModalSubTitle>Booking option*</ModalSubTitle>
                    <Controller
                        name="bookingOption"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                            <>
                                {hasOnlyOneBookingOption && (
                                    <Typography variant="body2">
                                        This event has only one booking option. Selected by default.
                                    </Typography>
                                )}
                                <AutoComplete
                                    value={value}
                                    disabled={editMode || hasOnlyOneBookingOption}
                                    options={availableBookingOptions}
                                    onChange={onChange}
                                    name={name}
                                />
                            </>
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <ModalSubTitle>Expected purchase price</ModalSubTitle>
                    <Controller
                        name="expectedPurchasePrice"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => {
                            const typedError = error as PriceError | undefined;

                            return (
                                <div data-cy="expected-purchase-price">
                                    <OptionalPriceInput
                                        price={value}
                                        onChange={(price: Price) => onChange(price)}
                                        currencyOptions={ticketSalesCapCurrencyOptions}
                                        defaultCurrency={defaultCurrency}
                                        valueError={typedError?.value.message}
                                        alignCurrencyLeft={true}
                                    />
                                </div>
                            );
                        }}
                    />
                </Grid>

                <Grid item xs={6} className={classes.spacingRight}>
                    <ModalSubTitle>Sales Cap*</ModalSubTitle>
                    <Controller
                        name="salesCap"
                        control={control}
                        render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                            <>
                                <Input
                                    type="number"
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    placeholder="Sales Cap"
                                    data-cy="sales-cap"
                                />
                                <FormFieldError message={error?.message} />
                            </>
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ModalSubTitle>Fixed Quantity Step</ModalSubTitle>
                    <Controller
                        name="fixedQuantityStep"
                        control={control}
                        render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                            <>
                                <Input
                                    type="number"
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    placeholder="Defaults to 1"
                                    data-cy="splits-count"
                                />
                                <FormFieldError message={error?.message} />
                            </>
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.blockHeader}>
                        <ModalSubTitle>Sales profiles*</ModalSubTitle>
                        <LinkActionButton
                            onClick={() =>
                                setValue('salesProfiles', salesProfileOptions, {
                                    shouldValidate: true,
                                })
                            }
                        >
                            Select all
                        </LinkActionButton>
                    </div>
                    <Controller
                        name="salesProfiles"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                            <AutoComplete
                                value={value}
                                options={salesProfileOptions}
                                onChange={onChange}
                                name={name}
                                isClearable={false}
                                isMulti
                                data-cy={`sales-profiles-${value}`}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.blockHeader}>
                        <ModalSubTitle>Retail Price*</ModalSubTitle>
                        <FormControlLabel
                            className={classNames(classes.smallFontSize, classes.samePriceCheckbox)}
                            control={
                                <Checkbox
                                    checked={useSamePrice}
                                    onChange={(_, checked: boolean) =>
                                        onUseSamePriceChange(checked)
                                    }
                                />
                            }
                            label="Same price for all enabled sales profiles"
                        />
                    </div>
                    {useSamePrice ? (
                        <Controller
                            name="retailPrice"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => {
                                const typedError = error as PriceError | undefined;

                                return (
                                    <div data-cy="retail-price">
                                        {renderPriceInput({
                                            price: value,
                                            onChange: (price: Price) => onChange(price),
                                            valueError: typedError?.value?.message,
                                            alignCurrencyLeft: true,
                                            currencyOptions: ticketSalesCapCurrencyOptions,
                                        })}
                                    </div>
                                );
                            }}
                        />
                    ) : (
                        salesProfilePrices.map(({ salesProfileId }, index) => (
                            <Grid
                                container
                                alignItems="center"
                                key={salesProfileId}
                                className={classes.salesProfilePrice}
                            >
                                <Grid item xs={4} className={classes.spacingRight}>
                                    <Typography variant="body2">
                                        {salesProfileOptions.find((p) => p.value === salesProfileId)
                                            ?.label ?? salesProfileId}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Controller
                                        name={`salesProfilePrices.${index}.price`}
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState: { error },
                                        }) => {
                                            const typedError = error as PriceError | undefined;

                                            return (
                                                <div data-cy={`retail-price-${salesProfileId}`}>
                                                    {renderPriceInput({
                                                        price: value,
                                                        onChange: (price: Price) => onChange(price),
                                                        valueError: typedError?.value?.message,
                                                        alignCurrencyLeft: true,
                                                        currencyOptions:
                                                            ticketSalesCapCurrencyOptions,
                                                    })}
                                                </div>
                                            );
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        ))
                    )}
                    {salesProfilePrices.length === 0 && (
                        <Typography variant="body2">
                            Select sales profiles to set their prices.
                        </Typography>
                    )}
                </Grid>
            </Grid>
            <FormButtons
                buttons={[
                    {
                        children: editMode ? 'Update Ticket Sales Cap' : 'Create Ticket Sales Cap',
                        onClick: handleSubmit(onFormSubmit),
                        disabled: isSubmitButtonDisabled,
                        dataCy: 'update-or-create-tickets-sales-cap-button',
                    },
                ]}
            />
        </>
    );
}
